import { LitElement } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { megaMenuTemplate } from './mega-menu.template';
import { styles } from './mega-menu.styles';
import { fontsInitializer } from '../../shared/utils/sbd/sbd.initializer';
import { transversalJSON } from '../../shared/constants/transversal.json';
import type {
  MenuItem,
  Category,
  RightAction,
  Option,
  ActionButton,
  MegaOptionDetail,
  ProjectId,
  MenuEventAnalytics
} from '../../shared/types';
import { utilSheet } from '../../shared/utils/styles/utils.styles';
import { BREAKPOINTS, responsiveSheet } from '../../shared/utils/styles/responsive.styles';
import {
  customOptionsConverter,
  lowerStringConverter,
  booleanConverter
} from '../../shared/utils/converters';
import { sanitizeEnum, sanitizeRightActions } from '../../shared/utils/validators';
import { AuthItem } from './user-area/user-area';

const ALLOWED = ['inmobiliario', 'movilidad', 'compras'] as const;
/* -----------------------------------------------------------
 * Web‑Component
 * ---------------------------------------------------------*/
export class MegaMenu extends LitElement {
  @query('#submenu-left') private submenuLeftEl!: HTMLElement;
  @query('#submenu-right') private submenuRightEl!: HTMLElement;
  @query('#mobile-nav') private mobileNavEl!: HTMLElement;
  @query('#core-options') private coreOptionsEl!: HTMLElement;

  private _project: ProjectId = 'inmobiliario';
  private _customOptions: RightAction[] = [];
  private _docClickOpts = { capture: true } as const;
  private _userMenuItems: AuthItem[] = [];
  private viewportMediaQuery: MediaQueryList | null = null;
  private viewportListener?: EventListener;
  /* ---------- Public API ---------- */

  @property({ type: Boolean, reflect: true, attribute: 'user-auth' })
  authenticated = false;

  @property({ type: String, attribute: 'user-name' })
  userName = '';

  @property({ converter: booleanConverter, reflect: false, attribute: 'show-login-button' })
  showLoginButton = true;

  /**
   * Entradas adicionales que el host puede inyectar; se concatenan al menú
   * principal (útil para features contextuales).
   */

  @property({
    attribute: 'project',
    reflect: true,
    noAccessor: true,
    converter: lowerStringConverter // normaliza el atributo a lower-case
  })
  get project(): ProjectId {
    return this._project;
  }
  set project(v: ProjectId | string) {
    const old = this._project;
    this._project = sanitizeEnum<ProjectId>(v, ALLOWED, 'inmobiliario', 'project');
    this.requestUpdate('project', old);
  }

  @property({
    attribute: 'custom-options',
    reflect: false,
    converter: customOptionsConverter
  })
  get customOptions(): RightAction[] {
    return this._customOptions;
  }
  set customOptions(val: RightAction[]) {
    const old = this._customOptions;
    this._customOptions = sanitizeRightActions(val, 2, 'mega-menu custom-options');
    this.requestUpdate('customOptions', old);
  }

  @property({
    attribute: 'user-menu-items',
    reflect: true,
    converter: (value: string) => JSON.parse(value) as AuthItem[]
  })
  get userMenuItems(): AuthItem[] {
    return this._userMenuItems;
  }
  set userMenuItems(val: AuthItem[]) {
    const old = this._userMenuItems;
    this._userMenuItems = val;
    this.requestUpdate('userMenuItems', old);
  }

  /* ---------- Internal reactive state ---------- */
  @state() private menuItems: MenuItem[] = [...(transversalJSON as MenuItem[])];
  @state() private selectedOption?: MenuItem;
  @state() private selectedCategory?: Category;
  @state() private submenuOpen = false;
  @state() private isUserMenuOpen = false;
  @state() private isCompactViewport = true;

  static styles = [styles, utilSheet, responsiveSheet];

  private get defaultActions(): RightAction[] {
    return [
      {
        id: 'login',
        label: this.authenticated ? this.userName || 'Cuenta' : 'Iniciar sesión',
        icon: this.authenticated ? 'user' : 'user'
      }
    ];
  }

  /* --------------------------- Lifecycle --------------------------- */
  connectedCallback(): void {
    super.connectedCallback();
    fontsInitializer();
    this.syncViewportMode();
    this._onDocClick = this._onDocClick.bind(this);
    this._onKey = this._onKey.bind(this);
    document.addEventListener('click', this._onDocClick, this._docClickOpts);
    document.addEventListener('keydown', this._onKey);
    this.initScrollListener();
    this.initViewportListener();
  }
  disconnectedCallback(): void {
    document.removeEventListener('click', this._onDocClick, this._docClickOpts);
    document.removeEventListener('keydown', this._onKey);
    window.removeEventListener('scroll', this.handleScroll);
    this.cleanupViewportListener();
    super.disconnectedCallback();
  }

  private getViewportMediaQuery(): string {
    return `(min-width: ${BREAKPOINTS.desktopXS}px)`;
  }

  private computeIsCompactViewport(): boolean {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return true;
    }

    return !window.matchMedia(this.getViewportMediaQuery()).matches;
  }

  private syncViewportMode(): void {
    this.isCompactViewport = this.computeIsCompactViewport();
  }

  private initViewportListener(): void {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    this.viewportMediaQuery = window.matchMedia(this.getViewportMediaQuery());
    this.viewportListener = ((event: MediaQueryListEvent) => {
      const nextIsCompactViewport = !event.matches;

      if (nextIsCompactViewport === this.isCompactViewport) {
        return;
      }

      this.isCompactViewport = nextIsCompactViewport;
      this.resetViewportState();
    }) as EventListener;

    this.viewportMediaQuery.addEventListener('change', this.viewportListener);
  }

  private cleanupViewportListener(): void {
    if (this.viewportMediaQuery && this.viewportListener) {
      this.viewportMediaQuery.removeEventListener('change', this.viewportListener);
    }

    this.viewportMediaQuery = null;
    this.viewportListener = undefined;
  }

  private resetBodyStyles(): void {
    document.body.style.position = '';
    document.body.style.overflow = '';
  }

  private resetViewportState(): void {
    this.submenuOpen = false;
    this.selectedOption = undefined;
    this.selectedCategory = undefined;
    this.isUserMenuOpen = false;
    this.resetBodyStyles();
  }

  private _onDocClick(e: MouseEvent) {
    if (!this.submenuOpen) return;
    const path = e.composedPath();
    const clickedInsideMenu =
      (this.submenuLeftEl || this.submenuRightEl || this.mobileNavEl) &&
      (path.includes(this.submenuLeftEl) ||
        path.includes(this.submenuRightEl) ||
        path.includes(this.mobileNavEl));
    const clickedCoreOption = this.coreOptionsEl && path.includes(this.coreOptionsEl);

    // Solo cierra si el click fue fuera del menú y fuera de las opciones principales
    if (!clickedInsideMenu && !clickedCoreOption) {
      this._closeSubmenu();
    }
  }
  private _onKey(e: KeyboardEvent) {
    if (e.key === 'Escape') this._closeSubmenu();
  }

  /* ---------------------- Event Handlers --------------------------- */
  private _handleRightAction(e: MouseEvent, option: RightAction): void {
    e.preventDefault();
    this.dispatchEvent(
      new CustomEvent('rightOption', { bubbles: true, composed: true, detail: option })
    );
    this._dispatchAnalyticsEvent({
      elemento: option.label
    });
  }

  private _onLoginClick(): void {
    this.dispatchEvent(new CustomEvent('login', { bubbles: true, composed: true }));
  }

  private _onCoreOptionSelected(index: number, id: string, device: 'desktop' | 'mobile'): void {
    const candidate = this.menuItems[index];
    if (device === 'desktop') {
      // Si la misma opción está activa → cierra el submenú
      document.body.style.position = 'fixed';
      if (this.selectedOption?.id === id) {
        this._closeSubmenu();
        return;
      }
      this.selectedOption = { ...candidate, id };
      this.selectedCategory = undefined;
      this.submenuOpen = true;
    }

    if (device === 'mobile') {
      if (this.selectedOption?.title === candidate?.title) {
        this.selectedOption = undefined;
        return;
      }
      this.selectedOption = { ...candidate, id };
      document.body.style.overflow = 'hidden'; // Deshabilita el scroll del body
    }
    this._dispatchAnalyticsEvent({
      elemento: candidate.title
    });
  }

  private _onCategorySelected(cat: Category): void {
    // Si la misma categoría está activa → cierra solo la categoría
    if (this.selectedCategory?.title === cat?.title) {
      this.selectedCategory = undefined;
      return;
    }
    this.selectedCategory = { ...cat };
    this._dispatchAnalyticsEvent({
      elemento: cat.title,
      tab: this.selectedOption?.id || ''
    });
  }

  private _closeSubmenu(): void {
    this.submenuOpen = false;
    this.selectedOption = undefined;
    this.selectedCategory = undefined;
    this.resetBodyStyles();
  }

  private _openNav(): void {
    if (this.submenuOpen) {
      document.body.style.position = '';
      document.body.style.overflow = '';
    } else {
      document.body.style.position = 'fixed';
    }
    this.submenuOpen = !this.submenuOpen;
  }

  private _emitMegaOption(detail: MegaOptionDetail, analytics: MenuEventAnalytics): void {
    this.dispatchEvent(
      new CustomEvent('megaOption', {
        detail,
        bubbles: true,
        composed: true
      })
    );

    this._dispatchAnalyticsEvent(analytics);
    this._closeSubmenu();
  }

  private _onLeafOptionClick(e: MouseEvent, option: Option, submenu: string): void {
    e.preventDefault();
    this._emitMegaOption(option, {
      elemento: option.title,
      tab: this.selectedOption?.id || '',
      menu: this.selectedCategory?.title || '',
      submenu: submenu || ''
    });
  }

  private _onActionButtonClick(
    e: MouseEvent,
    actionButton: ActionButton,
    categoryTitle: string
  ): void {
    e.preventDefault();
    this._emitMegaOption(actionButton, {
      elemento: actionButton.title,
      tab: this.selectedOption?.id || '',
      menu: categoryTitle || this.selectedCategory?.title || ''
    });
  }

  // Toggle y selección dentro del menú autenticado
  private _toggleUserMenu = () => {
    this.isUserMenuOpen = !this.isUserMenuOpen;
  };

  private _onUserMenuSelect = (item: AuthItem) => {
    this.isUserMenuOpen = false;
    this.dispatchEvent(
      new CustomEvent('userMenuOption', {
        bubbles: true,
        composed: true,
        detail: item
      })
    );
  };

  private _normalizeText(text: string): string {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private _dispatchAnalyticsEvent(data: MenuEventAnalytics) {
    const normalizedData = Object.entries(data).reduce((acc, [key, value]) => {
      acc[key as keyof MenuEventAnalytics] =
        typeof value === 'string' ? this._normalizeText(value) : value;
      return acc;
    }, {} as MenuEventAnalytics);

    this.dispatchEvent(
      new CustomEvent('dataLayerInfo', {
        bubbles: true,
        composed: true,
        detail: { ...normalizedData }
      })
    );
  }

  /* ----------------------------- Scroll Handling ---------------------------- */
  private lastScrollY = 0;
  private ticking = false;

  private initScrollListener() {
    this.handleScroll = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
  }

  private handleScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        this.updateMenuVisibility();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  private updateMenuVisibility() {
    const currentScrollY = window.scrollY;
    const container = this.shadowRoot?.querySelector('.mega-menu-principal-container');

    if (!container) return;

    // Scroll hacia abajo - esconder menú
    if (currentScrollY > this.lastScrollY && currentScrollY > 100) {
      container.classList.add('hidden');
      container.classList.remove('visible');
    }
    // Scroll hacia arriba - mostrar menú
    else if (currentScrollY < this.lastScrollY) {
      container.classList.remove('hidden');
      container.classList.add('visible');
    }

    this.lastScrollY = currentScrollY;
  }

  /* ----------------------------- Render ---------------------------- */
  render() {
    return megaMenuTemplate({
      menuItems: [...this.menuItems],
      selectedOption: this.selectedOption,
      selectedCategory: this.selectedCategory,
      project: this.project,
      isCompactViewport: this.isCompactViewport,
      submenuOpen: this.submenuOpen,
      rightActions: [...this.customOptions, ...this.defaultActions],
      authenticated: this.authenticated,
      userName: this.userName,
      isUserMenuOpen: this.isUserMenuOpen,
      userMenuItems: this.userMenuItems,
      showLoginButton: this.showLoginButton,
      onToggleUserMenu: this._toggleUserMenu,
      onUserMenuSelect: this._onUserMenuSelect,
      onLogin: () => this._onLoginClick(),
      onCoreSelect: (idx: number, id: string, device: 'desktop' | 'mobile') =>
        this._onCoreOptionSelected(idx, id, device),
      onCategorySelect: (cat: Category) => this._onCategorySelected(cat),
      onClose: () => this._closeSubmenu(),
      onOpenNav: () => this._openNav(),
      onLeafClick: (event: MouseEvent, opt: Option, submenu: string) =>
        this._onLeafOptionClick(event, opt, submenu),
      onActionButtonClick: (event: MouseEvent, actionButton: ActionButton, categoryTitle: string) =>
        this._onActionButtonClick(event, actionButton, categoryTitle),
      onRigthClick: (e: MouseEvent, opt: RightAction) => this._handleRightAction(e, opt)
    });
  }
}

customElements.define('mega-menu', MegaMenu);
